import {
  SaveItemInput,
  SavedFolder,
  SavedItem,
  SavedLibrary,
} from '../types/savedItems';

const API_BASE_URL = '/api';

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.error === 'string' && body.error.trim()) {
      return body.error;
    }
  } catch {
    // ignore
  }
  return `${fallback} (${response.status})`;
}

async function parseJson<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    throw new Error(await readError(response, fallback));
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

async function authHeaders(getToken: () => Promise<string | null>): Promise<HeadersInit> {
  const token = await getToken();
  if (!token) {
    throw new Error('You need to sign in to save items.');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export async function fetchSavedLibrary(
  getToken: () => Promise<string | null>
): Promise<SavedLibrary> {
  const response = await fetch(`${API_BASE_URL}/me/saved`, {
    headers: await authHeaders(getToken),
  });
  return parseJson<SavedLibrary>(response, 'Failed to load saved items');
}

export async function createSavedFolder(
  getToken: () => Promise<string | null>,
  name: string
): Promise<SavedFolder> {
  const response = await fetch(`${API_BASE_URL}/me/saved/folders`, {
    method: 'POST',
    headers: await authHeaders(getToken),
    body: JSON.stringify({ name }),
  });
  return parseJson<SavedFolder>(response, 'Failed to create folder');
}

export async function renameSavedFolder(
  getToken: () => Promise<string | null>,
  id: string,
  name: string
): Promise<SavedFolder> {
  const response = await fetch(`${API_BASE_URL}/me/saved/folders/${id}`, {
    method: 'PATCH',
    headers: await authHeaders(getToken),
    body: JSON.stringify({ name }),
  });
  return parseJson<SavedFolder>(response, 'Failed to rename folder');
}

export async function reorderSavedFolders(
  getToken: () => Promise<string | null>,
  folderIds: string[]
): Promise<SavedLibrary> {
  const response = await fetch(`${API_BASE_URL}/me/saved/folders/order`, {
    method: 'PUT',
    headers: await authHeaders(getToken),
    body: JSON.stringify({ folderIds }),
  });
  return parseJson<SavedLibrary>(response, 'Failed to reorder folders');
}

export async function placeSavedItem(
  getToken: () => Promise<string | null>,
  itemId: string,
  folderId: string,
  index: number
): Promise<SavedLibrary> {
  const response = await fetch(`${API_BASE_URL}/me/saved/items/place`, {
    method: 'PUT',
    headers: await authHeaders(getToken),
    body: JSON.stringify({ itemId, folderId, index }),
  });
  return parseJson<SavedLibrary>(response, 'Failed to move saved item');
}

export async function saveSavedItem(
  getToken: () => Promise<string | null>,
  input: SaveItemInput
): Promise<SavedItem> {
  const response = await fetch(`${API_BASE_URL}/me/saved/items`, {
    method: 'POST',
    headers: await authHeaders(getToken),
    body: JSON.stringify(input),
  });
  return parseJson<SavedItem>(response, 'Failed to save item');
}

export async function deleteSavedItem(
  getToken: () => Promise<string | null>,
  id: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/me/saved/items/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(getToken),
  });
  await parseJson<void>(response, 'Failed to remove saved item');
}
